import readline from "node:readline";
import { getValidToken, loadAuth } from "../lib/auth.js";

/**
 * `clawnify mcp` — stdio bridge to mcp.clawnify.com.
 *
 * Reads newline-delimited JSON-RPC messages from stdin (the MCP
 * stdio transport convention), forwards each as an HTTP POST to
 * mcp.clawnify.com/mcp with the stored Bearer token, and writes
 * the JSON-RPC response back to stdout.
 *
 * Used by bash-less MCP clients (Claude Desktop, ChatGPT custom
 * apps) per docs/internal/agent-tiering.md § "Surface 3 — MCP". Bash-capable
 * agents should use the CLI directly (clawnify execute / docs /
 * sdk inspect) — same auth, fewer hops.
 *
 * Notifications (no `id`) yield 202 with no body; we emit nothing.
 * Errors during forwarding emit a JSON-RPC error response with the
 * original request id so the client surfaces them cleanly.
 */

const MCP_URL = "https://mcp.clawnify.com/mcp";

export async function mcpBridgeCommand() {
  const token = await getValidToken();
  if (!token) {
    process.stderr.write("Not logged in. Run `clawnify login` first.\n");
    process.exit(1);
  }

  const auth = loadAuth();
  const baseHeaders: Record<string, string> = {
    "content-type": "application/json",
    authorization: "Bearer " + token,
  };
  if (auth?.org_id) {
    baseHeaders["X-Clawnify-Org-Id"] = auth.org_id;
  }

  const rl = readline.createInterface({ input: process.stdin, terminal: false });

  rl.on("line", async (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    let parsedId: string | number | null = null;
    try {
      parsedId = (JSON.parse(trimmed) as { id?: string | number | null }).id ?? null;
    } catch {
      // Malformed JSON — let the gateway surface the parse error;
      // it'll respond with a JSON-RPC parse error of its own.
    }

    try {
      const res = await fetch(MCP_URL, {
        method: "POST",
        headers: baseHeaders,
        body: trimmed,
      });
      const text = await res.text();
      // 202 (notification ack) returns empty body — emit nothing.
      if (text.length > 0) {
        process.stdout.write(text);
        if (!text.endsWith("\n")) process.stdout.write("\n");
      }
    } catch (err) {
      const errResp = {
        jsonrpc: "2.0",
        id: parsedId,
        error: { code: -32603, message: "bridge transport error: " + String(err) },
      };
      process.stdout.write(JSON.stringify(errResp) + "\n");
    }
  });

  rl.on("close", () => {
    process.exit(0);
  });
}
